import {Validation} from "@/data/Validation";

/**
 * Query key of the menu in the admin. Not `menu`: the POS caches its own
 * (converted) menu under that key.
 */
export const ADMIN_MENU_QUERY_KEY = "admin-menu";

/**
 * Days of a category's availability, as the backend expects them.
 */
export const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

export type Day = typeof DAYS[number];

/**
 * All flags a product can have. `alcoholic` marks drinks with alcohol (age
 * check).
 */
export const FLAGS = [
    // Taste warnings
    "alcoholic", "hot", "spicy", "mild",
    // Allergens
    "egg", "wheat", "lupine", "lactose", "mustard", "nuts", "peanut", "shellfish", "celery", "sesame",
    "soya", "fish", "mollusc", "sulphite",
    // Dietary preference
    "vegetarian", "vegan", "halal",
] as const;

export type FlagName = typeof FLAGS[number];

export const VAT_RATES = [0, 9, 21] as const;

export type OptionGroupType = "required" | "optional";

/**
 * Amounts are sent as a string with a dot and at most 2 decimals, e.g.
 * `"12.50"` (the same format as the menu returns them).
 */
export type Amount = string;

/**
 * All editable fields of a product, to create or update it at once. Images
 * can't be changed (yet).
 */
export type ProductEditValues = {
    name: string,
    description: string | null, // `null` for none, never an empty string
    price: Amount, // Delivery
    priceTakeaway: Amount,
    deposit: Amount,
    supFee: Amount,
    vat: number, // 0, 9 or 21
    soldout: boolean,
    delivery: boolean,
    takeaway: boolean,
    flags: FlagName[],
    optionGroupIds: number[], // In the order to show them
    categoryIds: number[], // A new category gets the product at the end
};

export type DayAvailabilityEditValues = {
    day: Day,
    disabled: boolean, // Not available on this day at all; then no times
    timeFrom: string | null, // `HHmm`, e.g. `1600`; `null` for start of the day
    timeUntil: string | null, // `HHmm`, e.g. `2200`; `null` for end of the day
};

/**
 * All editable fields of a category. Products left out of `productIds` are
 * removed from the category (not deleted).
 */
export type CategoryEditValues = {
    name: string,
    subtitle: string, // Empty for none
    availability: DayAvailabilityEditValues[], // A day without entry is available all day
    productIds: number[], // In the order to show them
};

/**
 * Fields of an option group. Options are added and removed separately.
 */
export type OptionGroupEditValues = {
    name: string,
    type: OptionGroupType,
    maxOptions: number, // Only for `optional`, 0 = unlimited
    // Options in the order to show them; left out = removed from the group
    // (not deleted). Leave out (`undefined`) to keep the options as they are.
    optionIds?: number[],
};

/**
 * All editable fields of an option. An option in several groups is changed in
 * all of them.
 */
export type ProductOptionEditValues = {
    name: string,
    price: Amount, // Delivery // Allowed to be negative
    priceTakeaway: Amount, // Allowed to be negative
    deposit: Amount,
    supFee: Amount,
    vat: number, // 0, 9 or 21
    soldout: boolean,
    delivery: boolean,
    takeaway: boolean,
};

export const productEditValuesFormValidation: Validation<ProductEditValues> = {
    name: {required: true, minLength: 1, maxLength: 1024},
    description: {required: false, minLength: 1, maxLength: 1024},
    price: {required: true, min: 0},
    priceTakeaway: {required: true, min: 0},
    deposit: {required: false, min: 0}, // Empty is 0,00
    supFee: {required: false, min: 0}, // Empty is 0,00
    vat: {required: true},
    soldout: {required: false},
    delivery: {required: false},
    takeaway: {required: false},
    flags: {required: false},
    optionGroupIds: {required: false},
    categoryIds: {required: false},
};

export const categoryEditValuesFormValidation: Validation<CategoryEditValues> = {
    name: {required: true, minLength: 1, maxLength: 1024},
    subtitle: {required: false, maxLength: 1024},
    availability: {required: false},
    productIds: {required: false},
};

export const optionGroupEditValuesFormValidation: Validation<OptionGroupEditValues> = {
    name: {required: true, minLength: 1, maxLength: 128},
    type: {required: true},
    maxOptions: {required: true, min: 0},
};

export const productOptionEditValuesFormValidation: Validation<ProductOptionEditValues> = {
    name: {required: true, minLength: 1, maxLength: 1024},
    price: {required: true},
    priceTakeaway: {required: true},
    deposit: {required: true, min: 0},
    supFee: {required: true, min: 0},
    vat: {required: true},
    soldout: {required: false},
    delivery: {required: false},
    takeaway: {required: false},
};

/**
 * Turns an amount from an input (e.g. `"12,5"`) into the format of the backend
 * (`"12.50"`). Empty is `"0.00"`.
 */
export const toAmount = (value: string | number | null | undefined): Amount => (
    Number(String(value ?? "").trim().replace(",", ".") || "0").toFixed(2)
);